// Image geometry belongs to message metadata, not to the download lifecycle.
import type { CSSProperties } from "react";
import { useState } from "react";

export interface MediaPreviewProps {
  alt: string;
  thumbnailSrc?: string;
  width?: number;
  height?: number;
  unavailable?: boolean;
  onOpen?: () => void;
}

export function MediaPreview({
  alt,
  thumbnailSrc,
  width,
  height,
  unavailable = false,
  onOpen,
}: MediaPreviewProps) {
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const loaded = !!thumbnailSrc && loadedSrc === thumbnailSrc;
  const failed = unavailable || (!!thumbnailSrc && failedSrc === thumbnailSrc);
  // Missing/invalid metadata uses a stable frame; decoding never resizes it.
  const ratio =
    width && height && Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
      ? width / height
      : 4 / 3;
  const frame: CSSProperties = {
    position: "relative",
    display: "block",
    width: `min(var(--chat-media-preview-max-width), 100%, calc(var(--chat-media-preview-max-height) * ${ratio}))`,
    aspectRatio: String(ratio),
    overflow: "hidden",
    borderRadius: "var(--chat-radius-md)",
    background: "var(--chat-surface-panel)",
    padding: 0,
    border: "none",
  };
  const body = failed ? (
    <span
      role="img"
      aria-label={alt ? `Image unavailable: ${alt}` : "Image unavailable"}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--chat-fg-secondary)",
        fontSize: "var(--chat-type-body-xs-size)",
      }}
    >
      Image unavailable
    </span>
  ) : thumbnailSrc ? (
    <img
      src={thumbnailSrc}
      alt={alt}
      decoding="async"
      onLoad={() => setLoadedSrc(thumbnailSrc)}
      onError={() => setFailedSrc(thumbnailSrc)}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "contain",
        // An <img> keeps painting its previous picture until a new src has
        // decoded, so after the first load a swap (sending → sent) never blinks.
        visibility: loadedSrc !== null ? "visible" : "hidden",
      }}
    />
  ) : null;
  return (
    <figure style={{ margin: 0 }}>
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          disabled={!loaded || failed}
          className="chat-media-preview chat-button"
          aria-label={alt ? `Open image: ${alt}` : "Open image"}
          style={{ ...frame, cursor: loaded && !failed ? "zoom-in" : "default" }}
        >
          {body}
        </button>
      ) : (
        <span style={frame}>{body}</span>
      )}
      {alt && (
        <figcaption
          style={{
            marginTop: "var(--chat-space-1)",
            color: "var(--chat-fg-muted)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-xs-size)",
            lineHeight: "var(--chat-type-body-xs-line)",
          }}
        >
          {alt}
        </figcaption>
      )}
    </figure>
  );
}
